import { z } from 'zod'
import { GLOBAL_CONSTANTS } from '@/constants/globalConstants'

const EMAIL_VALIDATION_REGEX = new RegExp(
  GLOBAL_CONSTANTS.BASIC_EMAIL_VALIDATION_REGEX
)

// Basic validation utilities
export const validateField = (value, rules) => {
  if (!rules) return null

  // Required validation
  if (rules.required && (!value || value.toString().trim() === '')) {
    return rules.message || 'This field is required'
  }

  // Skip other validations if field is empty and not required
  if (!value || value.toString().trim() === '') {
    return null
  }

  // Pattern validation
  if (rules.pattern && !rules.pattern.test(value)) {
    return rules.message || 'Invalid format'
  }

  // Min length validation
  if (rules.minLength && value.toString().length < rules.minLength) {
    return rules.message || `Minimum length is ${rules.minLength}`
  }

  // Max length validation
  if (rules.maxLength && value.toString().length > rules.maxLength) {
    return rules.message || `Maximum length is ${rules.maxLength}`
  }

  return null
}

export const validateForm = (formData, validationRules) => {
  const errors = {}
  let isValid = true

  Object.keys(validationRules).forEach(fieldName => {
    const error = validateField(formData[fieldName], validationRules[fieldName])
    if (error) {
      errors[fieldName] = error
      isValid = false
    }
  })

  return { errors, isValid }
}

export const numericUpTo10DigitsOptionalField = label => {
  return z
    .string()
    .trim()
    .max(10, `${label} cannot exceed 10 digits`)
    .regex(
      /^$|^\d{1,10}$/,
      `${label} must be numeric and cannot exceed 10 digits`
    )
    .nullable()
    .optional()
}

export const numericUpTo10DigitsRequiredField = label => {
  return z
    .string({ required_error: 'Number is required' })
    .trim()
    .min(1, `${label} is required`)
    .max(10, `${label} cannot exceed 10 characters`)
    .regex(
      /^[0-9]{1,10}$/,
      `${label} must be numeric and cannot exceed 10 digits`
    )
}

export const emailValidationOptional = label => {
  return z
    .string()
    .max(75, `${label} cannot exceed 75 characters`)
    .refine(val => !val || EMAIL_VALIDATION_REGEX.test(val), {
      message: 'Invalid email format',
    })
    .nullable()
    .optional()
}

export const emailValidationRequired = label => {
  return z
    .string({ required_error: `${label} is required` })
    .min(1, `${label} is required`)
    .max(75, `${label} cannot exceed 75 characters`)
    .refine(val => !val || EMAIL_VALIDATION_REGEX.test(val), {
      message: 'Invalid email format',
    })
}
